import React, { FunctionComponent } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, CssBaseline, styled } from '@mui/material';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';

import { backdropImageUrl } from '@paragon/client-tools';
import { isUsingCustomTheme, LoginContainer } from '@paragon/login-tools';
import { ParagonHeader } from '@paragon/material';
import { Notifier, NotifierDialog } from '@paragon/notification-tools';
import { getSpinnersToDisplay, SpinnerMessage } from '@paragon/task-spinner';
import { ThemedLogo, UserDropdown } from '@paragon/themes';

import pricingLogoUrl from '../../resources/pricing.png';
import startUp from '../actions/startApp';
import { RootState } from '../store';
import Content from './Content';
import ExportButton from './header/ExportButton';
import ImportButton from './header/ImportButton';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent': { color: 'white' },
}));

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

const RootComponent: FunctionComponent<Props> = (props) => {
  const messages = Array.from(props.spinnerMessages).map((text) => <SpinnerMessage text={text} key={text} />);
  const renderHeaderText = () => {
    if (props.isUsingCustomTheme) {
      return `Pricing`;
    }

    return 'PARAGON PRICING';
  };
  return (
    <>
      <CssBaseline />
      <SnackbarProvider
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        maxSnack={3}
        Components={{
          default: StyledMaterialDesignContent,
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
          warning: StyledMaterialDesignContent,
          info: StyledMaterialDesignContent,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            position: 'fixed',
            top: 0,
            left: 0,
            backgroundImage: `url(${backdropImageUrl})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundAttachment: 'fixed',
          }}
        >
          <LoginContainer
            onLogIn={props.startUp}
            clientLogoUrl={pricingLogoUrl}
            logoOverride={(props.isUsingCustomTheme && <ThemedLogo width={300} />) || undefined}
            clientName="Paragon Pricing"
            requirePermission="pricing-access"
          >
            <Notifier />
            <NotifierDialog />
            <ParagonHeader clientLogoUrl={pricingLogoUrl} clientName="paragon-pricing" text={renderHeaderText()}>
              <ImportButton />
              <ExportButton />
              <UserDropdown hideThemeToggleButton />
            </ParagonHeader>
            {messages}
            <Content />
          </LoginContainer>
        </Box>
      </SnackbarProvider>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  spinnerMessages: getSpinnersToDisplay(state),
  activePriceGrid: state.view.activePriceGrid,
  isUsingCustomTheme: isUsingCustomTheme(state),
});

const actionCreators = {
  startUp,
};

export default connect(mapStateToProps, actionCreators)(RootComponent);
